<template>
  <div class="w-full bg-slate-50 min-h-screen p-4" style="font-family: 'Inter', sans-serif;">
    <div class="max-w-7xl mx-auto space-y-6">
      
      <!-- HEADER -->
      <div class="mb-6">
        <h1 class="text-2xl font-bold text-slate-700">Locador Prestador (TCE)</h1>
        <p class="text-sm text-slate-500">Consulta e exportação de dados para o Tribunal de Contas</p>
      </div>

      <!-- FILTROS -->
      <section class="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
        <div class="px-6 py-4 flex items-center gap-3" style="background-color: #333333;">
          <v-icon color="orange" small>mdi-filter-variant</v-icon>
          <h2 class="text-white font-bold uppercase tracking-widest text-[11px]">FILTROS DE BUSCA</h2>
        </div>

        <div class="p-6">
          <div class="grid grid-cols-1 md:grid-cols-12 gap-6">
            <!-- Mês e Ano -->
            <div class="md:col-span-3">
              <label class="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2">Mês e Ano</label>
              <div class="relative flex items-center">
                <v-icon small color="#94a3b8" class="absolute left-3 z-10">mdi-calendar</v-icon>
                <input 
                  type="month" 
                  v-model="filter.date"
                  class="w-full pl-10 pr-3 py-2 bg-white border border-slate-300 rounded-lg text-sm text-slate-700 focus:outline-none focus:ring-1 focus:ring-orange-500 focus:border-orange-500 transition-all h-[40px]"
                >
              </div>
            </div>

            <!-- Prefeitura -->
            <div class="md:col-span-12 lg:col-span-9">
              <label class="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2">Prefeitura</label>
              <v-autocomplete
                :items="prefectures"
                v-model="filter.prefecture_id"
                item-value="prefecture_id"
                item-text="prefecture_social_name"
                @change="getCode(filter.prefecture_id)"
                placeholder="Selecione a prefeitura..."
                outlined
                dense
                hide-details
                class="custom-input"
              />
            </div>

            <!-- Código Unidade Gestora -->
            <div class="md:col-span-12">
              <label class="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2">Código da Unidade Gestora</label>
              <v-autocomplete
                :items="codigos"
                v-model="filter.cod"
                item-value="cod"
                item-text="cod"
                placeholder="Selecione o código..."
                outlined
                dense
                hide-details
                class="custom-input"
              />
            </div>
          </div>

          <!-- Ações de Filtro -->
          <div class="flex justify-end gap-3 mt-8 pt-6 border-t border-slate-100">
            <v-btn
              color="orange"
              class="white--text font-bold text-[11px] tracking-widest h-10 px-8 rounded-lg"
              depressed
              :to="`/report/tce/locador-prestador/${filter.date?.substring(5)}/${filter.date?.substring(0, 4)}/${filter.prefecture_id}/${filter.cod}`"
              target="_blank"
              :disabled="!filter.prefecture_id || !filter.cod || !filter.date"
            >
              <v-icon left small>mdi-file-pdf-box</v-icon>
              GERAR RELATÓRIO
            </v-btn>
            
            <button 
              @click="search"
              :disabled="isButtonDisabled"
              class="bg-slate-700 hover:bg-slate-800 disabled:opacity-50 text-white font-bold text-[11px] px-8 py-2 rounded-lg shadow-md transition-all uppercase tracking-widest inline-flex items-center justify-center gap-2 h-10"
            >
              <v-icon small color="white">mdi-magnify</v-icon>
              FILTRAR
            </button>
          </div>
        </div>
      </section>

      <!-- RESULTADO -->
      <section v-if="data.length > 0 || loader" class="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
        <div class="px-6 py-4 flex items-center justify-between" style="background-color: #333333;">
          <div class="flex items-center gap-3">
            <v-icon color="orange" small>mdi-table</v-icon>
            <h2 class="text-white font-bold uppercase tracking-widest text-[11px]">RESULTADO DA CONSULTA</h2>
          </div>
          <button 
            @click="exportFile"
            class="bg-white/10 hover:bg-white/20 text-white font-bold text-[10px] px-6 py-2 rounded-lg border border-white/20 transition-all uppercase tracking-widest inline-flex items-center gap-2"
          >
            <v-icon small color="white">mdi-export-variant</v-icon>
            EXPORTAR TXT
          </button>
        </div>

        <div class="p-0">
          <v-data-table 
            :loading="loader" 
            :items="data" 
            :headers="headers"
            class="custom-table"
            hide-default-footer
            :items-per-page="-1"
          >
             <template v-slot:item.cpfcnpj="{ item }">
                <span class="text-xs font-mono text-slate-500">{{ item.cpfcnpj }}</span>
              </template>
              <template v-slot:item.nome="{ item }">
                <span class="text-sm font-bold text-slate-700 uppercase">{{ item.nome }}</span>
              </template>
          </v-data-table>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import Helpers from "@/mixins/helpers.js";
export default {
  mixins: [Helpers],
  data() {
    return {
      loader: false,
      data: [],
      prefectures: [],
      codigos: [],
      filter: {
        prefecture_id: null,
        date: null,
        cod: null,
      },
      headers: [
        { text: "Código da Unidade Gestora", value: "codUnidadeGestora" },
        { text: "Cpf/Cnpj do Proprietário", value: "cpfcnpj" },
        { text: "Nome do Proprietário", value: "nome" },
      ],
    };
  },
  created() {
    this.Helpers;
    this.initialize();
  },
  computed: {
    isButtonDisabled() {
      return !(
        this.filter.prefecture_id &&
        this.filter.date &&
        this.filter.cod
      );
    },
  },
  methods: {
    initialize() {
      this.loader = true;
      this.$store.dispatch("setLoader", this.loader);
      this.$http.get("/prefectures").then((r) => (this.prefectures = r.data));
      this.loader = false;
      this.$store.dispatch("setLoader", this.loader);
    },
    getCode(prefecture_id) {
      this.$http(`/tce/codigo/${prefecture_id}`).then(
        (r) => (this.codigos = r.data)
      );
    },
    search() {
      this.loader = true;
      this.$store.dispatch("setLoader", this.loader);
      this.$http
        .get(
          `/tce/locador-prestador/${this.filter.prefecture_id}/${this.filter.cod}`
        )
        .then((res) => {
          this.data = res.data;
          this.loader = false;
          this.$store.dispatch("setLoader", this.loader);
        })
        .catch((e) => {
          this.loader = false;
          this.$store.dispatch("setLoader", this.loader);
          alert(e.message);
        });
    },
    exportFile() {
      const element = document.createElement("a");
      const content = this.data.map((value, index) => {
        if (index > 0) {
          return `\n${value.export}`;
        }
        return value.export;
      });

      const file = new Blob(content, { type: "text/plain" });
      element.href = URL.createObjectURL(file);
      element.download = `${this.filter.cod}${this.filter.date.substring(
        5
      )}${this.filter.date.substring(0, 4)}LocadorPrestador.txt`;
      document.body.appendChild(element);
      element.click();
    },
  },
};
</script>
<style scoped>
.min-h-screen { min-height: 100vh; }
.bg-slate-50 { background-color: #f8fafc; }
.w-full { width: 100%; }
.p-4 { padding: 1rem; }

/* Custom Table Override */
.custom-table ::v-deep table { background-color: transparent !important; border-collapse: separate; border-spacing: 0; }
.custom-table ::v-deep thead { background-color: #f8fafc !important; }
.custom-table ::v-deep th { 
  color: #64748b !important; 
  font-weight: 700 !important; 
  text-transform: uppercase !important; 
  font-size: 0.75rem !important; 
  background-color: #f8fafc !important;
  border-bottom: 1px solid #e2e8f0 !important;
  height: 48px !important;
}
.custom-table ::v-deep td { 
  border-bottom: 1px solid #f1f5f9 !important;
  color: #334155 !important;
  font-size: 0.875rem !important;
}

.custom-input ::v-deep .v-input__slot {
  background: white !important;
  border-radius: 8px !important;
}
.custom-input ::v-deep fieldset {
  border-color: #e2e8f0 !important;
}
</style>
